import { useState } from 'react';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { ModalBody, ModalFooter, Modal, Button, Label, Form, Row, Col, Input } from 'reactstrap';
import matriculacionApiService from '../../services/http/matriculacionApiService';
import useSoloLecturaMatriculas from '../../hooks/useSoloLecturaMatriculas';

// Estados internos que el backend acepta como notaRA ademas del rango 1-10.
const NOTAS_ESTADO = [-1, -3, -4, -5, -6];
const notaRAValida = (n) =>
  (Number.isInteger(n) && n >= 1 && n <= 10) || NOTAS_ESTADO.includes(n);

// Modal para añadir/actualizar una nota de RA (upsert en el backend). El
// desplegable de RA se rellena con `catalogoRAs` del modulo (se envia CodiRA).
const ModalNotasRA = ({ state, setState, catalogoRAs, idmatriculal, headquarter, onSaved }) => {
  const soloLectura = useSoloLecturaMatriculas();
  const [apiError, setApiError] = useState('');

  const validate = (values) => {
    const errors = {};
    if (!values.CodiRA) errors.CodiRA = 'Obligatorio';
    if (values.conv === '') errors.conv = 'Obligatorio';
    if (values.notaRA === '' || Number.isNaN(Number(values.notaRA))) {
      errors.notaRA = 'Obligatorio';
    } else if (!notaRAValida(Number(values.notaRA))) {
      errors.notaRA = 'Nota entre 1 y 10';
    }
    return errors;
  };

  const formik = useFormik({
    initialValues: { CodiRA: '', notaRA: '', conv: '' },
    validateOnChange: false,
    validate,
    onSubmit: async (values) => {
      setApiError('');
      try {
        await matriculacionApiService.crearNotaRA({
          headquarter,
          idmatriculal,
          CodiRA: values.CodiRA,
          notaRA: Number(values.notaRA),
          conv: Number(values.conv),
        });
        formik.resetForm();
        setState(false);
        if (onSaved) onSaved();
      } catch (error) {
        setApiError(error?.response?.data?.error || 'No se pudo guardar la nota de RA.');
      }
    },
  });

  const cerrar = () => {
    setApiError('');
    formik.resetForm();
    setState(false);
  };

  return (
    <Modal isOpen={state} centered>
      <ModalBody>
        <Label className="mb-3">
          <b>Añadir nota de RA</b>
        </Label>
        <Form>
          <Row className="mb-3">
            <Col sm="12">
              <Label htmlFor="CodiRA" className="form-label">
                <small>
                  RA <span className="text-danger">*</span>
                </small>
              </Label>
              <Input
                type="select"
                name="CodiRA"
                id="CodiRA"
                onChange={formik.handleChange}
                value={formik.values.CodiRA}
                className={formik.errors.CodiRA ? 'border border-danger error rounded ' : ''}
              >
                <option value="">Seleccione un RA</option>
                {catalogoRAs.map((ra) => (
                  <option key={ra.CodiRA} value={ra.CodiRA}>
                    {ra.CodiRA} - {ra.desc_cast || ra.desc_cat}
                  </option>
                ))}
              </Input>
              {formik.errors.CodiRA ? (
                <div className="text-danger tiny">{formik.errors.CodiRA}</div>
              ) : null}
            </Col>
          </Row>
          <Row className="mb-3">
            <Col sm="7">
              <Label htmlFor="conv" className="form-label">
                <small>
                  Convocatoria <span className="text-danger">*</span>
                </small>
              </Label>
              <Input
                type="select"
                name="conv"
                id="conv"
                onChange={formik.handleChange}
                value={formik.values.conv}
                className={formik.errors.conv ? 'border border-danger error rounded ' : ''}
              >
                <option value="">Seleccione una opción</option>
                <option value="1">Ordinaria</option>
                <option value="2">Extraordinaria</option>
              </Input>
              {formik.errors.conv ? (
                <div className="text-danger tiny">{formik.errors.conv}</div>
              ) : null}
            </Col>
            <Col sm="5">
              <Label htmlFor="notaRA" className="form-label">
                <small>
                  Nota <span className="text-danger">*</span>
                </small>
              </Label>
              <Input
                type="number"
                name="notaRA"
                id="notaRA"
                min="1"
                max="10"
                placeholder="Nota (1-10)"
                onChange={formik.handleChange}
                value={formik.values.notaRA}
                className={formik.errors.notaRA ? 'border border-danger error rounded ' : ''}
              />
              {formik.errors.notaRA ? (
                <div className="text-danger tiny">{formik.errors.notaRA}</div>
              ) : null}
            </Col>
          </Row>
          {apiError ? <div className="text-danger tiny mb-2">{apiError}</div> : null}
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button onClick={formik.handleSubmit} disabled={soloLectura}>
          Aceptar
        </Button>
        <Button color="danger" onClick={cerrar}>
          Cancelar
        </Button>
      </ModalFooter>
    </Modal>
  );
};

ModalNotasRA.propTypes = {
  state: PropTypes.bool,
  setState: PropTypes.func,
  catalogoRAs: PropTypes.array,
  idmatriculal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  headquarter: PropTypes.string,
  onSaved: PropTypes.func,
};

ModalNotasRA.defaultProps = {
  catalogoRAs: [],
};

export default ModalNotasRA;
